import { useState } from 'react'
import { Plus, FolderOpen } from 'lucide-react'
import { useSeries, useDeleteSeries } from '@/hooks/useSeries'
import { useSubscription } from '@/hooks/useSubscription'
import { SeriesCard } from './SeriesCard'
import { CreateSeriesModal } from './CreateSeriesModal'
import { UsageGate } from '@/components/subscription/UsageGate'
import { useNavigate } from 'react-router'
import type { Series } from '@/types/series'
import { useLabels, singular } from '@/lib/terminology'

export function SeriesList() {
  const navigate = useNavigate()
  const labels = useLabels()
  const { data: seriesList, isLoading } = useSeries()
  const { canCreateSeries, seriesRemaining } = useSubscription()
  const deleteSeries = useDeleteSeries()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingSeries, setEditingSeries] = useState<Series | null>(null)

  const handleSeriesClick = (seriesId: string) => {
    navigate(`/collections/${seriesId}`)
  }

  const handleDeleteSeries = async (seriesId: string) => {
    if (window.confirm(`Are you sure you want to delete this ${singular(labels.collections).toLowerCase()}? All ${labels.recordings.toLowerCase()} will also be deleted.`)) {
      await deleteSeries.mutateAsync(seriesId)
    }
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="animate-pulse p-5 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900"
          >
            <div className="h-32 mb-4 rounded-lg bg-zinc-200 dark:bg-zinc-800" />
            <div className="h-5 w-3/4 bg-zinc-200 dark:bg-zinc-800 rounded mb-2" />
            <div className="h-4 w-1/2 bg-zinc-200 dark:bg-zinc-800 rounded" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Create new series card */}
        <UsageGate
          type="series"
          renderBlocked={() => null}
        >
          <button
            onClick={() => setShowCreateModal(true)}
            className="p-5 rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700 bg-zinc-50/50 dark:bg-zinc-800/30 hover:border-zinc-400 dark:hover:border-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 transition-all flex flex-col items-center justify-center min-h-[200px] group"
          >
            <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Plus className="w-6 h-6 text-primary-500" />
            </div>
            <span className="font-medium text-zinc-700 dark:text-zinc-300">
              Create New {singular(labels.collections)}
            </span>
            {canCreateSeries && seriesRemaining > 0 && (
              <span className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                {seriesRemaining} remaining
              </span>
            )}
          </button>
        </UsageGate>

        {/* Series cards */}
        {seriesList?.map((series) => (
          <SeriesCard
            key={series.id}
            series={series}
            onClick={() => handleSeriesClick(series.id)}
            onEdit={() => setEditingSeries(series)}
            onDelete={() => handleDeleteSeries(series.id)}
          />
        ))}
      </div>

      {/* Empty state */}
      {seriesList?.length === 0 && (
        <div className="mt-6 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-12 text-center">
          <FolderOpen className="w-12 h-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-900 dark:text-zinc-100 font-medium mb-2">No {labels.collections.toLowerCase()} yet</p>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Create your first {singular(labels.collections).toLowerCase()} to organize your {labels.recordings.toLowerCase()}!
          </p>
        </div>
      )}

      {/* Create modal */}
      <CreateSeriesModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Edit modal */}
      <CreateSeriesModal
        isOpen={!!editingSeries}
        onClose={() => setEditingSeries(null)}
        series={editingSeries}
      />
    </>
  )
}
